import { FormEvent, useEffect, useState } from 'react'
import { Box, Button, MenuItem, Select } from '@mui/material'
import type { AssignmentAccess, AssignmentScope, Group } from 'chat-shared'

// Reicht sowohl für OrganizationSummary als auch AdminOrganizationEntry —
// beide haben mindestens id+name.
export type AssignableOrganization = { id: string; name: string }

export const scopeLabel = (
  scope: AssignmentScope,
  orgs: Array<AssignableOrganization>,
  groupsByOrg: Record<string, Array<Group>>,
): string => {
  if (scope.type === 'organization') {
    const org = orgs.find((o) => o.id === scope.organizationId)
    return `Organisation „${org?.name ?? scope.organizationId}“ (${scope.access === 'write' ? 'Lesen & Schreiben' : 'Nur Lesen'})`
  }
  const group = Object.values(groupsByOrg).flat().find((g) => g.id === scope.groupId)
  return `Gruppe „${group?.name ?? scope.groupId}“ (${scope.access === 'write' ? 'Lesen & Schreiben' : 'Nur Lesen'})`
}

type AssignmentPickerProps = {
  orgs: Array<AssignableOrganization>
  groupsByOrg: Record<string, Array<Group>>
  onLoadGroups: (orgId: string) => void
  onSubmit: (scope: AssignmentScope) => void
}

export const AssignmentPicker = ({ orgs, groupsByOrg, onLoadGroups, onSubmit }: AssignmentPickerProps) => {
  const [type, setType] = useState<'organization' | 'group'>('organization')
  const [organizationId, setOrganizationId] = useState('')
  const [groupOrgId, setGroupOrgId] = useState('')
  const [groupId, setGroupId] = useState('')
  const [access, setAccess] = useState<AssignmentAccess>('write')

  useEffect(() => {
    if (type === 'group' && groupOrgId) onLoadGroups(groupOrgId)
  }, [type, groupOrgId])

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (type === 'organization' && organizationId) {
      onSubmit({ type: 'organization', organizationId, access })
    } else if (type === 'group' && groupId) {
      onSubmit({ type: 'group', groupId, access })
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={1}>
      <Select size="small" value={type} onChange={(e) => setType(e.target.value as 'organization' | 'group')}>
        <MenuItem value="organization">Organisation</MenuItem>
        <MenuItem value="group">Gruppe</MenuItem>
      </Select>
      {type === 'organization' ? (
        <Select size="small" value={organizationId} onChange={(e) => setOrganizationId(e.target.value)} displayEmpty>
          <MenuItem value="" disabled>
            Organisation wählen
          </MenuItem>
          {orgs.map((org) => (
            <MenuItem key={org.id} value={org.id}>
              {org.name}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <>
          <Select
            size="small"
            value={groupOrgId}
            onChange={(e) => {
              setGroupOrgId(e.target.value)
              setGroupId('')
            }}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Organisation wählen
            </MenuItem>
            {orgs.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            displayEmpty
            disabled={!groupOrgId}
          >
            <MenuItem value="" disabled>
              Gruppe wählen
            </MenuItem>
            {(groupsByOrg[groupOrgId] ?? []).map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
              </MenuItem>
            ))}
          </Select>
        </>
      )}
      <Select size="small" value={access} onChange={(e) => setAccess(e.target.value as AssignmentAccess)}>
        <MenuItem value="write">Lesen &amp; Schreiben</MenuItem>
        <MenuItem value="read">Nur Lesen</MenuItem>
      </Select>
      <Button type="submit" variant="contained" size="small">
        Zuweisen
      </Button>
    </Box>
  )
}
